import React, { useState } from "react";
import SuperAdminDevNavigation from "./SuperAdminDevNavigation";
import {
  Building2,
  Users,
  CreditCard,
  Settings,
  TrendingUp,
  DollarSign,
  Shield,
  AlertCircle,
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  Download,
  BarChart3,
  UserCheck,
  Activity,
  X,
} from "lucide-react";

interface Organization {
  id: string;
  name: string;
  industry: string;
  size: string;
  plan: string;
  status: "active" | "suspended" | "trial";
  createdAt: string;
  lastActive: string;
  users: number;
  teams: number;
  monthlyRevenue: number;
  adminEmail: string;
  website?: string;
}

interface Plan {
  id: string;
  name: string;
  type: "free" | "professional" | "enterprise";
  price: number;
  billing: "monthly" | "yearly";
  features: string[];
  limits: {
    users: number;
    teams: number;
    storage: string;
    support: string;
  };
  popular?: boolean;
}

interface SystemMetrics {
  totalOrganizations: number;
  totalUsers: number;
  totalRevenue: number;
  activeTrials: number;
  conversionRate: number;
  churnRate: number;
  averageTeamsPerOrg: number;
  supportTickets: number;
}

const SuperAdminDashboardDemo: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    "overview" | "organizations" | "plans" | "analytics" | "settings"
  >("overview");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<
    "all" | "active" | "suspended" | "trial"
  >("all");
  const [filterPlan, setFilterPlan] = useState<
    "all" | "free" | "professional" | "enterprise"
  >("all");
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization | null>(null);

  // Demo data for organizations
  const [organizations] = useState<Organization[]>([
    {
      id: "1",
      name: "TechCorp Solutions",
      industry: "Technology",
      size: "Large (500+ employees)",
      plan: "enterprise",
      status: "active",
      createdAt: "2024-01-15",
      lastActive: "2025-08-07",
      users: 450,
      teams: 25,
      monthlyRevenue: 2250,
      adminEmail: "admin@techcorp.com",
      website: "https://techcorp.com",
    },
    {
      id: "2",
      name: "StartupXYZ",
      industry: "Fintech",
      size: "Small (10-50 employees)",
      plan: "professional",
      status: "active",
      createdAt: "2024-06-20",
      lastActive: "2025-08-08",
      users: 35,
      teams: 4,
      monthlyRevenue: 299,
      adminEmail: "founder@startupxyz.com",
      website: "https://startupxyz.com",
    },
    {
      id: "3",
      name: "Design Studio Pro",
      industry: "Design",
      size: "Medium (50-200 employees)",
      plan: "professional",
      status: "trial",
      createdAt: "2025-07-25",
      lastActive: "2025-08-07",
      users: 85,
      teams: 8,
      monthlyRevenue: 0,
      adminEmail: "admin@designstudio.com",
      website: "https://designstudio.com",
    },
    {
      id: "4",
      name: "Manufacturing Inc",
      industry: "Manufacturing",
      size: "Large (500+ employees)",
      plan: "enterprise",
      status: "suspended",
      createdAt: "2023-11-10",
      lastActive: "2025-07-15",
      users: 320,
      teams: 18,
      monthlyRevenue: 0,
      adminEmail: "it@manufacturing.com",
    },
    {
      id: "5",
      name: "Consulting Group",
      industry: "Consulting",
      size: "Medium (50-200 employees)",
      plan: "free",
      status: "active",
      createdAt: "2025-05-12",
      lastActive: "2025-08-06",
      users: 15,
      teams: 2,
      monthlyRevenue: 0,
      adminEmail: "admin@consulting.com",
    },
  ]);

  // Demo data for plans
  const [plans] = useState<Plan[]>([
    {
      id: "1",
      name: "Free",
      type: "free",
      price: 0,
      billing: "monthly",
      features: [
        "Up to 5 users",
        "Basic kanban boards",
        "Email support",
        "Basic reporting",
      ],
      limits: {
        users: 5,
        teams: 1,
        storage: "1GB",
        support: "Email",
      },
    },
    {
      id: "2",
      name: "Professional",
      type: "professional",
      price: 19,
      billing: "monthly",
      popular: true,
      features: [
        "Up to 50 users",
        "Advanced kanban boards",
        "Sprint planning",
        "Advanced reporting",
        "Priority support",
        "Integrations",
      ],
      limits: {
        users: 50,
        teams: 10,
        storage: "100GB",
        support: "Priority Email + Chat",
      },
    },
    {
      id: "3",
      name: "Enterprise",
      type: "enterprise",
      price: 49,
      billing: "monthly",
      features: [
        "Unlimited users",
        "Custom workflows",
        "Advanced analytics",
        "SSO integration",
        "Dedicated support",
        "Custom integrations",
        "Advanced security",
      ],
      limits: {
        users: 999999,
        teams: 999999,
        storage: "Unlimited",
        support: "24/7 Phone + Dedicated Manager",
      },
    },
  ]);

  // Demo system metrics
  const [metrics] = useState<SystemMetrics>({
    totalOrganizations: 1247,
    totalUsers: 15680,
    totalRevenue: 245670,
    activeTrials: 89,
    conversionRate: 23.5,
    churnRate: 4.2,
    averageTeamsPerOrg: 8.3,
    supportTickets: 24,
  });

  // Filter organizations based on search and filters
  const filteredOrganizations = organizations.filter((org) => {
    const matchesSearch =
      !searchTerm ||
      org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.industry.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.adminEmail.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === "all" || org.status === filterStatus;
    const matchesPlan = filterPlan === "all" || org.plan === filterPlan;

    return matchesSearch && matchesStatus && matchesPlan;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusBadge = (status: Organization["status"]) => {
    const styles = {
      active: "bg-green-100 text-green-800",
      trial: "bg-blue-100 text-blue-800",
      suspended: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getPlanBadge = (plan: string) => {
    const styles = {
      free: "bg-gray-100 text-gray-800",
      professional: "bg-purple-100 text-purple-800",
      enterprise: "bg-yellow-100 text-yellow-800",
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          styles[plan as keyof typeof styles] || "bg-gray-100 text-gray-800"
        }`}
      >
        {plan.charAt(0).toUpperCase() + plan.slice(1)}
      </span>
    );
  };

  // Rest of the component implementation (overview, organizations, plans, analytics, settings tabs)
  // This is kept as demo data for reference and testing

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Organizations</p>
              <p className="text-2xl font-bold text-gray-900">
                {metrics.totalOrganizations.toLocaleString()}
              </p>
            </div>
            <Building2 className="h-8 w-8 text-blue-600" />
          </div>
          <p className="text-xs text-gray-500 mt-2">+12% from last month</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">
                {metrics.totalUsers.toLocaleString()}
              </p>
            </div>
            <Users className="h-8 w-8 text-green-600" />
          </div>
          <p className="text-xs text-gray-500 mt-2">+8% from last month</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Monthly Revenue
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(metrics.totalRevenue)}
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-purple-600" />
          </div>
          <p className="text-xs text-gray-500 mt-2">+15% from last month</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Trials</p>
              <p className="text-2xl font-bold text-gray-900">
                {metrics.activeTrials}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-orange-600" />
          </div>
          <p className="text-xs text-gray-500 mt-2">+5% from last month</p>
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Conversion Rate
              </p>
              <p className="text-2xl font-bold text-green-600">
                {metrics.conversionRate}%
              </p>
            </div>
            <UserCheck className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Churn Rate</p>
              <p className="text-2xl font-bold text-red-600">
                {metrics.churnRate}%
              </p>
            </div>
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Teams/Org</p>
              <p className="text-2xl font-bold text-blue-600">
                {metrics.averageTeamsPerOrg}
              </p>
            </div>
            <Activity className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Support Tickets
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {metrics.supportTickets}
              </p>
            </div>
            <Shield className="h-8 w-8 text-gray-600" />
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Recent Activity
          </h3>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <Building2 className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-900">
                  New organization <strong>TechCorp Solutions</strong> signed up
                  for Enterprise plan
                </p>
                <p className="text-xs text-gray-500">2 hours ago</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-900">
                  <strong>Manufacturing Inc</strong> account suspended due to
                  payment failure
                </p>
                <p className="text-xs text-gray-500">6 hours ago</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-900">
                  <strong>Design Studio Pro</strong> upgraded from Free to
                  Professional
                </p>
                <p className="text-xs text-gray-500">1 day ago</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderOrganizations = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Organizations</h2>
          <p className="text-sm text-gray-600">
            Manage all organizations and their subscriptions
          </p>
        </div>
        <button className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-2" />
          Add Organization
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search organizations..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
              value={filterStatus}
              onChange={(e) =>
                setFilterStatus(
                  e.target.value as "all" | "active" | "suspended" | "trial"
                )
              }
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="trial">Trial</option>
              <option value="suspended">Suspended</option>
            </select>
            <select
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
              value={filterPlan}
              onChange={(e) =>
                setFilterPlan(
                  e.target.value as
                    | "all"
                    | "free"
                    | "professional"
                    | "enterprise"
                )
              }
            >
              <option value="all">All Plans</option>
              <option value="free">Free</option>
              <option value="professional">Professional</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
        </div>
      </div>

      {/* Organizations Table */}
      <div className="bg-white shadow-sm border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Active
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrganizations.map((org) => (
                <tr key={org.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {org.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {org.industry} • {org.size}
                      </div>
                      <div className="text-sm text-gray-500">
                        {org.adminEmail}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getPlanBadge(org.plan)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(org.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>{org.users} users</div>
                    <div className="text-gray-500">{org.teams} teams</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {org.monthlyRevenue > 0
                      ? formatCurrency(org.monthlyRevenue)
                      : "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(org.lastActive)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => setSelectedOrganization(org)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button className="text-gray-600 hover:text-gray-900">
                        <Edit className="h-4 w-4" />
                      </button>
                      <button className="text-red-600 hover:text-red-900">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderPlans = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">
            Plans Management
          </h2>
          <p className="text-sm text-gray-600">
            Manage subscription plans and pricing
          </p>
        </div>
        <button className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-2" />
          Create Plan
        </button>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`relative bg-white rounded-lg shadow-sm border-2 ${
              plan.popular ? "border-purple-500" : "border-gray-200"
            }`}
          >
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                  Most Popular
                </span>
              </div>
            )}
            <div className="p-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {plan.name}
                </h3>
                <div className="mt-2">
                  <span className="text-3xl font-bold text-gray-900">
                    ${plan.price}
                  </span>
                  <span className="text-gray-600">/{plan.billing}</span>
                </div>
              </div>
              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                    </div>
                    <span className="ml-3 text-sm text-gray-600">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="border-t pt-4">
                <div className="text-xs text-gray-500 space-y-1">
                  <div>Up to {plan.limits.users} users</div>
                  <div>{plan.limits.teams} teams</div>
                  <div>{plan.limits.storage} storage</div>
                  <div>{plan.limits.support}</div>
                </div>
              </div>
              <div className="mt-4 flex space-x-2">
                <button className="flex-1 text-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Edit
                </button>
                <button className="flex-1 text-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700">
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Analytics</h2>
          <p className="text-sm text-gray-600">
            System performance and business metrics
          </p>
        </div>
        <button className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
          <Download className="h-4 w-4 mr-2" />
          Export Report
        </button>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Revenue Trends
        </h3>
        <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
          <div className="text-center">
            <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">Revenue chart would go here</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            User Growth
          </h3>
          <div className="h-48 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
            <div className="text-center">
              <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">User growth chart</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Plan Distribution
          </h3>
          <div className="h-48 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
            <div className="text-center">
              <Activity className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">Plan distribution chart</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-900">System Settings</h2>
        <p className="text-sm text-gray-600">
          Configure system-wide settings and preferences
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            General Settings
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                System Name
              </label>
              <input
                type="text"
                defaultValue="Kanbello"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Default Trial Period (days)
              </label>
              <input
                type="number"
                defaultValue="14"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Support Email
              </label>
              <input
                type="email"
                defaultValue="support@kanbello.com"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Billing Settings
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Payment Gateway
              </label>
              <select className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500">
                <option>Stripe</option>
                <option>PayPal</option>
                <option>Square</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Currency
              </label>
              <select className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500">
                <option>USD</option>
                <option>EUR</option>
                <option>GBP</option>
              </select>
            </div>
            <div className="flex items-center">
              <input type="checkbox" className="mr-2" defaultChecked />
              <label className="text-sm text-gray-700">
                Enable automatic billing
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Development Navigation - Only show in development */}
      {process.env.NODE_ENV === "development" && <SuperAdminDevNavigation />}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">
            Super Admin Dashboard (Demo)
          </h1>
          <p className="text-gray-600">
            Manage organizations, plans, and system analytics with demo data
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("overview")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "overview"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <BarChart3 className="h-4 w-4 inline mr-2" />
              Overview
            </button>
            <button
              onClick={() => setActiveTab("organizations")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "organizations"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <Building2 className="h-4 w-4 inline mr-2" />
              Organizations
            </button>
            <button
              onClick={() => setActiveTab("plans")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "plans"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <CreditCard className="h-4 w-4 inline mr-2" />
              Plans
            </button>
            <button
              onClick={() => setActiveTab("analytics")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "analytics"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <TrendingUp className="h-4 w-4 inline mr-2" />
              Analytics
            </button>
            <button
              onClick={() => setActiveTab("settings")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "settings"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <Settings className="h-4 w-4 inline mr-2" />
              Settings
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && renderOverview()}
        {activeTab === "organizations" && renderOrganizations()}
        {activeTab === "plans" && renderPlans()}
        {activeTab === "analytics" && renderAnalytics()}
        {activeTab === "settings" && renderSettings()}

        {/* Organization Detail Modal */}
        {selectedOrganization && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Organization Details
                </h3>
                <button
                  onClick={() => setSelectedOrganization(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Name
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedOrganization.name}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Industry
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedOrganization.industry}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Size
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedOrganization.size}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Plan
                    </label>
                    <p className="text-sm text-gray-900">
                      {getPlanBadge(selectedOrganization.plan)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Status
                    </label>
                    <p className="text-sm text-gray-900">
                      {getStatusBadge(selectedOrganization.status)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Monthly Revenue
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedOrganization.monthlyRevenue > 0
                        ? formatCurrency(selectedOrganization.monthlyRevenue)
                        : "-"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Users
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedOrganization.users}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Teams
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedOrganization.teams}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Admin Email
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedOrganization.adminEmail}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Website
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedOrganization.website || "-"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Created At
                    </label>
                    <p className="text-sm text-gray-900">
                      {formatDate(selectedOrganization.createdAt)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Last Active
                    </label>
                    <p className="text-sm text-gray-900">
                      {formatDate(selectedOrganization.lastActive)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminDashboardDemo;
